import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Brain, Save, MessageSquare, Settings } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const AISettings: React.FC = () => {
  const [systemPrompt, setSystemPrompt] = useState('');
  const [allowPromotionalResponses, setAllowPromotionalResponses] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      // Load AI settings
      const { data: settings, error: settingsError } = await supabase
        .from('ai_settings')
        .select('*');

      if (settingsError) {
        console.error('Error loading AI settings:', settingsError);
        return;
      }

      // Process settings
      if (settings) {
        const promotionalSetting = settings.find(s => s.setting_key === 'allow_promotional_responses');
        if (promotionalSetting) {
          setAllowPromotionalResponses(promotionalSetting.setting_value);
        }
      }

      // Load system prompt from ai_overrides table (more flexible for text content)
      const { data: overrides, error: overrideError } = await supabase
        .from('ai_overrides')
        .select('*')
        .eq('keyword', 'system_prompt')
        .eq('is_active', true)
        .single();

      if (overrideError && overrideError.code !== 'PGRST116') {
        console.error('Error loading system prompt:', overrideError);
      } else if (overrides) {
        setSystemPrompt(overrides.override_response);
      } else {
        // Set default system prompt
        setSystemPrompt(`You are Madison, the AI chatbot for the CruzaSmart app. You're here to help people understand border wait times, rules, rewards, and how the app works.

You always explain things in a way that a smart 5th grader can understand — simple words, real-life examples, and a friendly tone.

You speak both English and Spanish fluently. You automatically switch to the user's preferred language or mix Spanglish when natural.

Your personality is:
- Ryan Reynolds (sarcastic, charming, witty)
- Eugenio Derbez (fun, expressive, animated)
- When asked for a joke, use Dave Chappelle's style — bold, sharp, and clever

Use emojis, short replies (2–4 sentences), and avoid technical jargon. Always be helpful, playful, and community-friendly.`);
      }

    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Error loading settings');
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      // Save promotional responses setting in ai_settings
      const { error: promotionalError } = await supabase
        .from('ai_settings')
        .upsert({
          setting_key: 'allow_promotional_responses',
          setting_value: allowPromotionalResponses,
        }, {
          onConflict: 'setting_key'
        });

      if (promotionalError) {
        throw promotionalError;
      }

      // Save system prompt in ai_overrides table
      const { error: promptError } = await supabase
        .from('ai_overrides')
        .upsert({
          keyword: 'system_prompt',
          override_response: systemPrompt,
          is_active: true,
        }, {
          onConflict: 'keyword'
        });

      if (promptError) {
        throw promptError;
      }

      toast.success('AI settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error saving settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Madison AI Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* System Prompt Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Madison System Prompt
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Define Madison's personality, behavior, and response style
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="system-prompt">System Prompt</Label>
            <Textarea
              id="system-prompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={12}
              className="mt-1"
              placeholder="Define Madison's personality and behavior..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              This prompt defines how Madison responds to users. Include personality traits, 
              language preferences, and behavior guidelines.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* AI Behavior Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            AI Behavior Settings
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Control how Madison interacts with users
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="promotional-responses">Allow Promotional Responses</Label>
              <p className="text-sm text-muted-foreground">
                Enable Madison to suggest sponsored content and ads in responses
              </p>
            </div>
            <Switch
              id="promotional-responses"
              checked={allowPromotionalResponses}
              onCheckedChange={setAllowPromotionalResponses}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={saveSettings} 
          disabled={isSaving}
          className="w-full sm:w-auto"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default AISettings;